import type { Dispatch, SetStateAction } from 'react';
import { normalizeAiosMultitaskToolInput } from '@/utils/command-tools';
import {
    buildSegmentsFromBlockMap,
    isCoordinatorDelegationTaskTool,
    readSubagentCallIdFromEvent,
    readSubagentTaggedFields,
    resolveSubagentPanelId,
    SubagentStreamState,
} from '@/utils/command-subagent-nesting';
import { ExtendedChatMessage, type StreamPhase } from './chat-types';
import { ContentSegment } from './message-parser';
import { serializeToolResultContent } from './message-utils';
import type { SelectionOption, SelectionRequest } from '@/types/command-selection';
import type { ContextUsage } from '@/types/command-agent';
import { parseContextUsage } from '@/utils/command-context-usage';
import type { StreamRecord, StreamSupervisor } from './stream-supervisor';

function replaceUrlInText(value: string, oldUrl: string, newUrl: string): string {
    if (!value || !oldUrl || !value.includes(oldUrl)) return value;
    return value.split(oldUrl).join(newUrl);
}

function parseSelectionFromPayload(payload: Record<string, unknown> | null | undefined): SelectionRequest | null {
    if (!payload || typeof payload !== 'object') return null;
    const rawOptions = Array.isArray(payload.options) ? payload.options : [];
    const options: SelectionOption[] = [];
    for (const opt of rawOptions) {
        if (!opt || typeof opt !== 'object') continue;
        const record = opt as Record<string, unknown>;
        const value = String(record.value ?? record.id ?? '').trim();
        const label = String(record.label ?? record.name ?? value).trim();
        if (!label) continue;
        const subtitle =
            typeof record.subtitle === 'string'
                ? record.subtitle
                : typeof record.description === 'string'
                  ? record.description
                  : undefined;
        options.push({
            value: value || label,
            label,
            ...(subtitle ? { subtitle } : {}),
            recommended: Boolean(record.recommended),
        });
    }

    if (options.length === 0) return null;

    return {
        toolCallId: typeof payload.tool_call_id === 'string' ? payload.tool_call_id : undefined,
        name: typeof payload.name === 'string' ? payload.name : undefined,
        prompt: typeof payload.prompt === 'string' ? payload.prompt : undefined,
        options,
        multiSelect: Boolean(payload.multi_select),
    };
}
export type SSEEventHandler = (event: any) => void;

export interface ContentBlock {
    index: number;
    type: 'text' | 'thinking' | 'tool_use' | 'tool_result' | 'observation' | 'selection_request';
    content: string;
    toolId?: string;
    toolName?: string;
    toolInput?: any;
    toolOutput?: string;
    /** tool_result arrived with is_error */
    toolError?: boolean;
    selection?: import('@/types/command-selection').SelectionRequest;
}

/** Failed tool calls render as output-error; the result text is the error, not a response. */
function applyToolErrors(segments: ContentSegment[], source: Map<number, ContentBlock>): ContentSegment[] {
    const errors = new Map<string, string | undefined>();
    for (const block of source.values()) {
        if (block.type !== 'tool_use' || !block.toolError || !block.toolId) continue;
        errors.set(String(block.toolId).trim(), block.toolOutput);
    }
    if (errors.size === 0) return segments;
    return segments.map((segment) => {
        if (segment.type !== 'tool' || !segment.tool) return segment;
        const id = String(segment.tool.toolCallId ?? '').trim();
        if (!errors.has(id)) return segment;
        const output = errors.get(id) ?? segment.tool.output;
        return {
            ...segment,
            tool: {
                ...segment.tool,
                state: 'output-error',
                errorText: output,
                output: undefined,
            },
        };
    });
}

/**
 * Split raw SSE output on newlines, strip `data:` prefixes, JSON-parse each frame
 * and dispatch to `onEvent`. Malformed frames are silently dropped — SSE tolerates
 * partial lines between chunks.
 */
export function processSSEChunk(chunk: string, onEvent: SSEEventHandler) {
    const lines = chunk.split('\n').filter(line => line.trim());
    for (const line of lines) {
        try {
            let jsonStr = line.trim();
            if (jsonStr.startsWith('data:')) {
                jsonStr = jsonStr.slice(5).trim();
            }
            if (jsonStr && jsonStr.startsWith('{')) {
                const event = JSON.parse(jsonStr);
                onEvent(event);
            }
        } catch {
            // Malformed line; skip silently.
        }
    }
}

/** Feed SSE chunks to `handler` while the supervisor tracks liveness and the end event for `record`. */
export function supervisedChunkHandler(supervisor: StreamSupervisor, record: StreamRecord, handler: SSEEventHandler) {
    return (chunk: string) => {
        supervisor.noteActivity(record);
        processSSEChunk(chunk, (event) => {
            if (supervisor.noteEvent(record, event)) handler(event);
        });
    };
}

interface CreateEventHandlerOptions {
    /** Assistant message id this stream is updating (top-level or nested branch). */
    systemMessageId: string;
    setMessages: Dispatch<SetStateAction<ExtendedChatMessage[]>>;
    onSessionTitle?: (sessionId: string, title: string) => void;
    /** When flipped to `true`, future events are ignored — used to freeze UI updates on session switch. */
    cancelledRef?: { current: boolean };
    setStreamingPhase?: Dispatch<SetStateAction<'working' | 'responding'>>;
    /**
     * Optional temp user message id. When `message_start` arrives with `user_message_id`,
     * the message's `id` is replaced with the real server id so subsequent refetches
     * dedupe naturally and downstream consumers can stop juggling temp vs real ids.
     */
    userMessageId?: string;
    /**
     * Fires once `message_start` resolves the real server id for the assistant message —
     * the caller (e.g. `finalizeMessage`) needs the new id because the message's `id`
     * field has been swapped out from under it.
     */
    onAssistantIdResolved?: (realId: string) => void;
    /** Same as `onAssistantIdResolved` but for the paired user message. */
    onUserIdResolved?: (realId: string) => void;
    /** Live context-window usage from the stream — keep the composer ring in sync. */
    onContextUsage?: (usage: ContextUsage) => void;
}

/**
 * Build an SSE event handler that incrementally populates an assistant chat message
 * (plus optionally its paired user message) from streaming blocks. Each handler owns
 * its own block Map so concurrent streams don't clobber each other's state.
 */
export function createEventHandler({
    systemMessageId,
    setMessages,
    onSessionTitle,
    cancelledRef,
    setStreamingPhase,
    userMessageId,
    onAssistantIdResolved,
    onUserIdResolved,
    onContextUsage,
}: CreateEventHandlerOptions): SSEEventHandler {
    // Track the live id of the assistant/user rows. `message_start` replaces the temp ids
    // with the real server ids — every later event has to look up the message by the
    // resolved id, otherwise text_delta updates would miss after the swap.
    let currentAssistantId = systemMessageId;
    let currentUserId = userMessageId;
    // Per-handler block state — prevents concurrent streams (send + reconnect) from clobbering each other.
    const blocks = new Map<number, ContentBlock>();
    const subagentState = new SubagentStreamState();
    // The reply's end message (`error` event: "User has interrupted.", "Model limit reached…").
    // Kept here so a later rebuild of the segments doesn't drop it.
    let endNotice: ContentSegment | null = null;
    const buildSegments = (): ContentSegment[] => {
        const rootSegments = applyToolErrors(buildSegmentsFromBlockMap(blocks), blocks);
        const segments = subagentState.attachToRootSegments(rootSegments);
        return endNotice ? [...segments, endNotice] : segments;
    };

    /** Patch the target assistant message whether it is top-level or nested in `branches`. */
    const updateMessage = (updater: (msg: ExtendedChatMessage) => ExtendedChatMessage) => {
        setMessages(prev => prev.map(msg => {
            if (msg.id === currentAssistantId) {
                return updater(msg);
            }
            if (msg.branches) {
                const branchIndex = msg.branches.findIndex(b => b.id === currentAssistantId);
                if (branchIndex !== -1) {
                    const updatedBranches = [...msg.branches];
                    updatedBranches[branchIndex] = updater(updatedBranches[branchIndex]);
                    return { ...msg, branches: updatedBranches };
                }
            }
            return msg;
        }));
    };

    const syncSegments = () => {
        const segments = buildSegments();
        const tools = segments.filter(s => s.type === 'tool').map(s => s.tool!);
        updateMessage(msg => ({
            ...msg,
            contentSegments: segments,
            tools: tools.length > 0 ? tools : msg.tools,
            isStreaming: true,
        }));
    };

    let nextBlockIndex = 0;
    let currentTextBlockIndex: number | null = null;
    const pendingToolResults = new Map<string, string>();
    const pendingToolErrors = new Set<string>();

    return (event: any) => {
        if (cancelledRef?.current) return;

        if (event.type === 'message_start' && (event.message?.id ?? event.id)) {
            nextBlockIndex = 0;
            currentTextBlockIndex = null;
            pendingToolResults.clear();
            pendingToolErrors.clear();
            blocks.clear();
            subagentState.clear();
            endNotice = null;
            const realMessageId: string = event.message?.id ?? event.id;
            const realUserMessageId: string | null =
                event.user_message_id ?? event.message?.user_message_id ?? null;
            // Snapshot the ids the handler is currently tracking before we mutate state —
            // the setMessages map runs after the let-bindings would otherwise advance, and
            // we need the old ids to find the rows.
            const prevAssistantId = currentAssistantId;
            const prevUserId = currentUserId;
            setMessages(prev => prev.map(msg => {
                if (msg.id === prevAssistantId) {
                    // Stamp parent_message_id so the merge/sort logic can anchor this row
                    // next to its user message even when the server refetch hasn't returned
                    // the assistant row yet (it stays in `localOnly` with no server match,
                    // so propagating from `serverMsg` later wouldn't help — we have to set
                    // it here, while we have `realUserMessageId` in scope).
                    // A re-attach replays the run from here, end message and phase included:
                    // drop what an earlier attempt left on the bubble ("No reply was received").
                    return {
                        ...msg,
                        id: realMessageId,
                        dbId: realMessageId,
                        streamError: undefined,
                        streamPhase: undefined,
                        ...(realUserMessageId && { parent_message_id: realUserMessageId }),
                    };
                }
                if (realUserMessageId && (msg.id === prevUserId || msg.id === realUserMessageId)) {
                    // The message this reply answers: its turn has come, so it is no longer queued.
                    return {
                        ...msg,
                        ...(msg.id === prevUserId && { id: realUserMessageId, dbId: realUserMessageId }),
                        ...(msg.status === 'QUEUED' && { status: undefined }),
                    };
                }
                if (msg.branches) {
                    return {
                        ...msg,
                        branches: msg.branches.map(branch =>
                            branch.id === prevAssistantId
                                ? {
                                    ...branch,
                                    id: realMessageId,
                                    dbId: realMessageId,
                                    streamError: undefined,
                                    streamPhase: undefined,
                                    ...(realUserMessageId && { parent_message_id: realUserMessageId }),
                                }
                                : branch,
                        ),
                    };
                }
                return msg;
            }));
            currentAssistantId = realMessageId;
            onAssistantIdResolved?.(realMessageId);
            if (realUserMessageId) {
                currentUserId = realUserMessageId;
                onUserIdResolved?.(realUserMessageId);
            }
        }

        // Sent while another reply was running: the server queued it, and its reply comes on a later
        // stream. Mark the message queued and drop the reply placeholder.
        if (event.type === 'message_queued') {
            const queuedId: string | null = typeof event.user_message_id === 'string' ? event.user_message_id : null;
            const prevUserId = currentUserId;
            const placeholderId = currentAssistantId;
            setMessages(prev => prev
                .filter(msg => msg.id !== placeholderId)
                .map(msg => (prevUserId && msg.id === prevUserId
                    ? { ...msg, ...(queuedId && { id: queuedId, dbId: queuedId }), status: 'QUEUED' }
                    : msg)));
            if (queuedId) {
                currentUserId = queuedId;
                onUserIdResolved?.(queuedId);
            }
            return;
        }

        if (event.type === 'tool_call') {
            setStreamingPhase?.('working');
            const panelId = resolveSubagentPanelId(event as Record<string, unknown>);
            const { subagent } = readSubagentTaggedFields(event as Record<string, unknown>);
            const name = event.name ?? 'unknown';
            const input = normalizeAiosMultitaskToolInput(
                name,
                (event.input ?? {}) as Record<string, unknown>,
            );
            const rawId = event.tool_call_id ?? event.id ?? `tool-${nextBlockIndex}`;
            const toolCallId = String(rawId).trim();

            if (panelId) {
                subagentState.addToolCall(panelId, toolCallId, name, input, subagent);
                syncSegments();
                return;
            }

            if (isCoordinatorDelegationTaskTool(name, input)) {
                subagentState.upsertDelegationTaskPanel(toolCallId, input);
            }

            currentTextBlockIndex = null;
            // Choice prompts render as option tiles, not a generic tool card.
            // Create the block immediately so tiles appear as soon as options parse.
            if (/ask_user_choice/i.test(name)) {
                const selection = parseSelectionFromPayload({
                    ...input,
                    name,
                    tool_call_id: toolCallId,
                }) ?? {
                    toolCallId,
                    name,
                    options: [],
                    multiSelect: Boolean((input as Record<string, unknown>).multi_select),
                    prompt:
                        typeof (input as Record<string, unknown>).prompt === 'string'
                            ? String((input as Record<string, unknown>).prompt)
                            : undefined,
                };
                blocks.set(nextBlockIndex, {
                    index: nextBlockIndex,
                    type: 'selection_request',
                    content: Object.keys(input).length ? JSON.stringify(input) : '',
                    toolId: toolCallId,
                    toolName: name,
                    selection,
                });
                nextBlockIndex += 1;
                syncSegments();
                return;
            }

            blocks.set(nextBlockIndex, {
                index: nextBlockIndex,
                type: 'tool_use',
                content: '',
                toolId: toolCallId,
                toolName: name,
                toolInput: input,
            });
            // Tool-result may arrive before its matching tool_call: drain the queue here.
            const pendingOutput = pendingToolResults.get(toolCallId);
            const pendingError = pendingToolErrors.has(toolCallId);
            if (pendingOutput !== undefined || pendingError) {
                const block = blocks.get(nextBlockIndex);
                if (block && block.type === 'tool_use') {
                    if (pendingOutput !== undefined) block.toolOutput = pendingOutput;
                    if (pendingError) block.toolError = true;
                }
                pendingToolResults.delete(toolCallId);
                pendingToolErrors.delete(toolCallId);
            }
            nextBlockIndex += 1;
            syncSegments();
        }

        if (event.type === 'tool_result') {
            const panelId = resolveSubagentPanelId(event as Record<string, unknown>);
            const rawId = event.tool_call_id ?? event.id ?? null;
            const toolCallId = rawId != null ? String(rawId).trim() : null;
            const payload = event.content ?? event.output;
            const outputStr = serializeToolResultContent(payload);

            if (panelId) {
                subagentState.addToolResult(panelId, toolCallId, outputStr);
                syncSegments();
                return;
            }
            const toolUseBlocks = Array.from(blocks.values())
                .filter((b): b is ContentBlock & { type: 'tool_use' } => b.type === 'tool_use')
                .sort((a, b) => a.index - b.index);
            const normalizeId = (id: string | undefined) => (id != null ? String(id).trim() : '');
            let targetBlock = toolCallId
                ? toolUseBlocks.find(b => normalizeId(b.toolId) === toolCallId)
                : null;
            if (!targetBlock) {
                targetBlock = toolUseBlocks.find(b => !b.toolOutput) ?? null;
            }
            const isError = Boolean(event.is_error);
            if (targetBlock) {
                targetBlock.toolOutput = outputStr;
                targetBlock.toolError = isError;
                syncSegments();
            } else if (toolCallId) {
                pendingToolResults.set(toolCallId, outputStr);
                if (isError) pendingToolErrors.add(toolCallId);
                else pendingToolErrors.delete(toolCallId);
            }
        }

        if (event.type === 'text_delta' && event.text != null) {
            setStreamingPhase?.('responding');
            const panelId = resolveSubagentPanelId(event as Record<string, unknown>);
            if (panelId) {
                subagentState.addTextDelta(panelId, event.text);
                syncSegments();
                return;
            }
            let textBlock =
                currentTextBlockIndex !== null ? blocks.get(currentTextBlockIndex) : null;
            if (!textBlock || textBlock.type !== 'text') {
                textBlock = { index: nextBlockIndex, type: 'text' as const, content: '' };
                blocks.set(nextBlockIndex, textBlock);
                currentTextBlockIndex = nextBlockIndex;
                nextBlockIndex += 1;
            }
            textBlock.content += event.text;
            syncSegments();
        }

        if (event.type === 'text' && event.text != null) {
            if (currentTextBlockIndex !== null) {
                const textBlock = blocks.get(currentTextBlockIndex);
                if (textBlock && textBlock.type === 'text') {
                    textBlock.content = event.text;
                    syncSegments();
                    return;
                }
            }
            const textBlock: ContentBlock = { index: nextBlockIndex, type: 'text', content: event.text };
            blocks.set(nextBlockIndex, textBlock);
            currentTextBlockIndex = nextBlockIndex;
            nextBlockIndex += 1;
            syncSegments();
        }

        if (event.type === 'subagent_start') {
            const subagentCallId = readSubagentCallIdFromEvent(event as Record<string, unknown>);
            const name = typeof event.name === 'string' ? event.name : 'subagent';
            if (subagentCallId) {
                subagentState.startRun(subagentCallId, name);
                syncSegments();
            }
            return;
        }

        if (event.type === 'subagent_end') {
            const subagentCallId = readSubagentCallIdFromEvent(event as Record<string, unknown>);
            if (subagentCallId) {
                const rawStatus = typeof event.status === 'string' ? event.status : 'completed';
                const status = (
                    ['completed', 'failed', 'interrupted', 'drained'].includes(rawStatus)
                        ? rawStatus
                        : 'completed'
                ) as 'completed' | 'failed' | 'interrupted' | 'drained';
                const error = typeof event.error === 'string' ? event.error : null;
                subagentState.endRun(subagentCallId, status, error);
                syncSegments();
            }
            return;
        }

        if (event.type === 'session_title') {
            onSessionTitle?.(event.session_id, event.title);
            return;
        }

        if (event.type === 'content_block_start') {
            const block = event.content_block;
            const index = event.index;

            if (block.type === 'text') {
                blocks.set(index, { index, type: 'text', content: block.text || '' });
                syncSegments();
            } else if (block.type === 'thinking') {
                blocks.set(index, { index, type: 'thinking', content: block.thinking || '' });
                if (block.thinking) {
                    updateMessage(msg => ({ ...msg, reasoning: block.thinking, isStreaming: true }));
                }
            } else if (block.type === 'tool_use') {
                const toolName = block.name || 'unknown';
                if (/ask_user_choice/i.test(toolName)) {
                    const toolCallId = String(block.id ?? `tool-${index}`).trim();
                    const input = (block.input ?? {}) as Record<string, unknown>;
                    const selection = parseSelectionFromPayload({
                        ...input,
                        name: toolName,
                        tool_call_id: toolCallId,
                    }) ?? {
                        toolCallId,
                        name: toolName,
                        options: [],
                    };
                    currentTextBlockIndex = null;
                    blocks.set(index, {
                        index,
                        type: 'selection_request',
                        content: Object.keys(input).length ? JSON.stringify(input) : '',
                        toolId: toolCallId,
                        toolName,
                        selection,
                    });
                    syncSegments();
                } else {
                    blocks.set(index, {
                        index,
                        type: 'tool_use',
                        content: '',
                        toolId: block.id,
                        toolName: block.name,
                        toolInput: block.input,
                    });
                    syncSegments();
                }
            } else if (block.type === 'tool_result') {
                blocks.set(index, { index, type: 'tool_result', content: block.content || '' });
            } else if (block.type === 'observation') {
                const observationContent = block.content || block.observation || '';
                blocks.set(index, { index, type: 'observation', content: observationContent });
                syncSegments();
            } else if (block.type === 'selection_request') {
                const selection = parseSelectionFromPayload(block as Record<string, unknown>);
                if (selection) {
                    currentTextBlockIndex = null;
                    blocks.set(index, {
                        index,
                        type: 'selection_request',
                        content: '',
                        selection,
                    });
                    syncSegments();
                }
            }
        }

        if (event.type === 'selection_request') {
            const selection = parseSelectionFromPayload(event as Record<string, unknown>);
            if (selection) {
                currentTextBlockIndex = null;
                blocks.set(nextBlockIndex, {
                    index: nextBlockIndex,
                    type: 'selection_request',
                    content: '',
                    selection,
                });
                nextBlockIndex += 1;
                syncSegments();
            }
        }

        if (event.type === 'content_block_delta') {
            const index = event.index;
            const delta = event.delta;
            const block = blocks.get(index);

            if (delta.type === 'text_delta' && block) {
                block.content += delta.text;
                syncSegments();
            } else if (delta.type === 'thinking_delta' && block) {
                block.content += delta.thinking;
                const allThinking = Array.from(blocks.values())
                    .filter(b => b.type === 'thinking')
                    .sort((a, b) => a.index - b.index)
                    .map(b => b.content)
                    .join('\n');
                updateMessage(msg => ({ ...msg, reasoning: allThinking, isStreaming: true }));
            } else if (delta.type === 'input_json_delta' && block) {
                const partial =
                    typeof delta.partial_json === 'string'
                        ? delta.partial_json
                        : typeof delta.text === 'string'
                          ? delta.text
                          : '';
                if (!partial) {
                    // no-op
                } else if (block.type === 'selection_request') {
                    block.content += partial;
                    try {
                        const parsed = JSON.parse(block.content) as Record<string, unknown>;
                        const selection = parseSelectionFromPayload({
                            ...parsed,
                            name: block.toolName,
                            tool_call_id: block.toolId,
                        });
                        if (selection) {
                            block.selection = selection;
                            syncSegments();
                        }
                    } catch {
                        // Incomplete JSON while streaming — wait for more deltas.
                    }
                } else if (block.type === 'tool_use' && /ask_user_choice/i.test(block.toolName || '')) {
                    block.content += partial;
                    try {
                        const parsed = JSON.parse(block.content) as Record<string, unknown>;
                        const selection = parseSelectionFromPayload({
                            ...parsed,
                            name: block.toolName,
                            tool_call_id: block.toolId,
                        });
                        if (selection) {
                            block.type = 'selection_request';
                            block.selection = selection;
                            syncSegments();
                        }
                    } catch {
                        // Incomplete JSON while streaming — wait for more deltas.
                    }
                }
            } else if (delta.type === 'tool_result_delta' && block) {
                block.content += delta.result || '';
            } else if (delta.type === 'observation_delta' && block) {
                const deltaContent = delta.content || delta.observation || delta.text || '';
                block.content += deltaContent;
                syncSegments();
            }
        }

        if (event.type === 'context_usage') {
            const contextUsage = parseContextUsage(event);
            if (contextUsage) {
                updateMessage((msg) => ({ ...msg, contextUsage }));
                onContextUsage?.(contextUsage);
            }
            return;
        }

        // "thinking" after setup and after tools finish, "finishing" once the answer is written and the
        // title and suggestions are being made. Labels the live line while no text is arriving.
        if (event.type === 'status') {
            if (resolveSubagentPanelId(event as Record<string, unknown>)) return;
            if (event.phase === 'thinking' || event.phase === 'finishing') {
                const phase: StreamPhase = event.phase;
                updateMessage((msg) => (msg.streamPhase === phase ? msg : { ...msg, streamPhase: phase }));
            }
            return;
        }

        if (event.type === 'suggestions') {
            const raw = event.suggestions;
            const suggestions = Array.isArray(raw)
                ? raw
                      .map((item) => (typeof item === 'string' ? item.trim() : ''))
                      .filter((item) => item.length > 0)
                : [];
            if (suggestions.length > 0) {
                updateMessage((msg) => ({ ...msg, suggestions }));
            }
        }

        if (event.type === 'error') {
            // The backend sends `text`; older shapes put it in `message` or `error.message`.
            const errorText = [event.text, event.message, event.error?.message, event.error].find(
                (value): value is string => typeof value === 'string' && value.trim().length > 0,
            );
            if (!errorText) return;

            const reason = typeof event.reason === 'string' && event.reason ? event.reason : undefined;
            endNotice = { type: 'error', content: errorText, ...(reason ? { reason } : {}) };
            const segments = buildSegments();
            updateMessage((msg) => ({
                ...msg,
                streamError: errorText,
                contentSegments: segments,
                isStreaming: false,
            }));
            return;
        }

        if (event.type === 'content_replace') {
            const oldUrl = typeof event.old_url === 'string' ? event.old_url : '';
            const newUrl = typeof event.new_url === 'string' ? event.new_url : '';
            if (!oldUrl || !newUrl) return;

            for (const block of blocks.values()) {
                if (block.content) {
                    block.content = replaceUrlInText(block.content, oldUrl, newUrl);
                }
                if (block.toolOutput) {
                    block.toolOutput = replaceUrlInText(block.toolOutput, oldUrl, newUrl);
                }
                if (typeof block.toolInput === 'string') {
                    block.toolInput = replaceUrlInText(block.toolInput, oldUrl, newUrl);
                }
            }

            const segments = buildSegments();
            const tools = segments.filter(s => s.type === 'tool').map(s => s.tool!);
            updateMessage(msg => ({
                ...msg,
                content: replaceUrlInText(msg.content ?? '', oldUrl, newUrl),
                contentSegments: segments,
                tools: tools.length > 0 ? tools : msg.tools,
            }));
            return;
        }

        if (event.type === 'message_stop') {
            updateMessage(msg => ({ ...msg, isStreaming: false }));
        }
    };
}
